/**
 * IT support request form for VIVA HUB (/hub/support)
 */

import React, { useState } from 'react'
import { hubSupportAPI } from '../../utils/hub/api'
import { useToast } from '../../contexts/ToastContext'
import {
  HUB_SUPPORT_EMAIL,
  HUB_SUPPORT_CATEGORIES,
} from '../../constants/hub-support'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

const initialForm = {
  category: '',
  subject: '',
  message: '',
  urgency: 'normal',
}

const selectClassName = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50'
)

export default function HubSupportPanel() {
  const { success: toastSuccess, error: toastError } = useToast()
  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!form.category) {
      toastError('Please select a support category.')
      return
    }
    if (form.subject.trim().length < 3) {
      toastError('Please enter a short subject (at least 3 characters).')
      return
    }
    if (form.message.trim().length < 10) {
      toastError('Please describe the issue (at least 10 characters).')
      return
    }

    setSubmitting(true)
    try {
      const result = await hubSupportAPI.submit({
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
        urgency: form.urgency,
      })
      toastSuccess(
        result.message ||
          `Support request sent to ${HUB_SUPPORT_EMAIL}. We will follow up by email.`
      )
      setForm(initialForm)
    } catch (err) {
      toastError(
        err.message || 'Could not send the support request. Please try emailing support directly.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card aria-labelledby="hub-support-form-heading">
      <CardHeader>
        <CardTitle id="hub-support-form-heading">Submit a request</CardTitle>
        <CardDescription>
          Describe the issue below. Your name and HUB login email are included automatically.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="rounded-md border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-foreground">
          You can also email the technical support team directly at{' '}
          <a
            href={`mailto:${HUB_SUPPORT_EMAIL}`}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary/90"
          >
            {HUB_SUPPORT_EMAIL}
          </a>
          .
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hub-support-category">
                Category <span className="text-destructive">*</span>
              </Label>
              <select
                id="hub-support-category"
                value={form.category}
                onChange={updateField('category')}
                className={selectClassName}
                required
                disabled={submitting}
              >
                <option value="">Select a topic…</option>
                {HUB_SUPPORT_CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hub-support-urgency">Urgency</Label>
              <select
                id="hub-support-urgency"
                value={form.urgency}
                onChange={updateField('urgency')}
                className={selectClassName}
                disabled={submitting}
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hub-support-subject">
              Subject <span className="text-destructive">*</span>
            </Label>
            <Input
              id="hub-support-subject"
              type="text"
              value={form.subject}
              onChange={updateField('subject')}
              maxLength={200}
              placeholder="Brief summary of the issue"
              required
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hub-support-message">
              Description <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="hub-support-message"
              value={form.message}
              onChange={updateField('message')}
              rows={5}
              maxLength={5000}
              placeholder="What happened? Include any error messages, which device or app, and steps already tried."
              required
              disabled={submitting}
            />
          </div>

          <div className="pt-1">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send support request'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
